import { type CSSProperties, forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "../lib/cn";

/**
 * Card variants.
 *
 * - `elevated` (default): the canonical white elevated card, rounded-xl,
 *   shadow-card, no border. Used for settings, modals, generic content.
 * - `brief`: the brief shell via CARD_CHROME (rounded-2xl), mist-dark border, layered
 *   radial-teal-tint + cream-to-sage linear background, two-layer shadow.
 *   The geometry-specific gradient lives in the variant style, but every
 *   colour stop references a token (color-mix with --color-* vars) so the
 *   palette stays canonical.
 * - `rail`: the AmbientPage rail card, rounded-xl, gray-100 border,
 *   shadow-lg, hover:shadow-xl lift. Same look as /plan's MicrostepCard.
 */
export type CardVariant = "elevated" | "brief" | "rail";

/**
 * The canonical card surface treatment: background, border, radius, shadow.
 * Change it here and it flows through every variant and every component
 * that composes it (BriefSurface, OutcomeCard, RailCard, PersonaCard, ...).
 * Spacing, layout, and interaction live with each consumer.
 *
 * The default surface is the ambient sage gradient over white. State chromes
 * are complete parallel treatments, not additive classes: a consumer picks
 * exactly one chrome rather than layering two.
 */
export const CARD_CHROME =
  "rounded-2xl border border-border-card bg-surface-elevated bg-gradient-card shadow-card";

/** Positive/connected state: quiet leaf wash, softened border, light lift. */
export const CARD_CHROME_CONNECTED =
  "rounded-2xl border border-leaf/35 bg-surface-elevated bg-gradient-card-connected shadow-card-connected";

/** Selected/active state: teal wash, teal border, teal ring glow. */
export const CARD_CHROME_ACTIVE =
  "rounded-2xl border border-teal-500 bg-surface-elevated bg-gradient-card-active shadow-card-active";

/** Hero placement: the default chrome with the deeper feature shadow. */
export const CARD_CHROME_RAISED =
  "rounded-2xl border border-border-card bg-surface-elevated bg-gradient-card shadow-raised";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  /** @deprecated prefer `variant`. When true behaves like `shadow-thrive`. */
  elevated?: boolean;
  interactive?: boolean;
}

export const CARD_VARIANT_CLASS: Record<CardVariant, string> = {
  elevated: "rounded-xl bg-surface-elevated shadow-card",
  /*
   * Brief shares the rest of the card system's chrome (rounded-2xl,
   * mist-dark/40 border, shadow-card) so it sits in the column as a
   * peer to Outcome / Plan / Environment / Summary instead of an
   * outlier. The editorial warmth lives entirely in two very light
   * corner radials applied via the variant style, the base is plain
   * white. Keeps the broadsheet hint without competing for attention.
   */
  brief: `relative overflow-hidden ${CARD_CHROME}`,
  // Unified with `elevated` and the WeatherCard chrome so the patient
  // /today top row reads as one consistent card system: rounded-2xl,
  // mist-dark border at 40% alpha, shadow-card lifting to
  // shadow-card-hover. The earlier `shadow-lg` was visibly heavier
  // than its neighbours and broke the row.
  rail: `group relative overflow-hidden ${CARD_CHROME} p-5 transition-shadow duration-200 hover:shadow-card-hover`,
};

/**
 * Style-only variant payload. Consumers that need a non-`<div>` root (e.g.
 * BriefSurface uses `<article>` for semantics) can spread these directly.
 */
export const CARD_VARIANT_STYLE: Partial<Record<CardVariant, CSSProperties>> = {
  /*
   * Two low-intensity corner radials over a white base: teal-400 at
   * ~10% top-right, leaf at ~6% bottom-left. Down from 40% / 22%, so
   * the card reads as white with a hint of editorial warmth at the
   * corners rather than as a saturated tan-sage panel. No box-shadow
   * here, the canonical shadow-card class on the chrome is enough.
   */
  brief: {
    // Inline backgroundImage replaces the chrome's class gradient, so the
    // ambient card gradient is re-stated here as the base layer under the
    // two corner radials.
    backgroundImage: [
      "radial-gradient(780px 380px at 88% -15%, color-mix(in srgb, var(--color-teal-400) 10%, transparent), transparent 60%)",
      "radial-gradient(520px 420px at -5% 115%, color-mix(in srgb, var(--color-teal-500) 5%, transparent), transparent 65%)",
      "var(--gradient-card)",
    ].join(", "),
  },
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, style, variant, elevated, interactive, ...rest },
  ref,
) {
  const resolvedVariant: CardVariant = variant ?? (elevated ? "elevated" : "elevated");
  const variantStyle = CARD_VARIANT_STYLE[resolvedVariant];
  return (
    <div
      ref={ref}
      className={cn(
        CARD_VARIANT_CLASS[resolvedVariant],
        elevated && !variant && "shadow-thrive",
        interactive && "transition-shadow duration-200 hover:shadow-card-hover cursor-pointer",
        className,
      )}
      style={variantStyle ? { ...variantStyle, ...style } : style}
      {...rest}
    />
  );
});

export function CardHeader({ children, className }: { children: ReactNode; className?: string }) {
  return <header className={cn("px-6 pt-6 pb-2", className)}>{children}</header>;
}

export function CardTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h3 className={cn("font-title text-xl text-ink-primary leading-tight", className)}>
      {children}
    </h3>
  );
}

export function CardBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("px-6 py-4", className)}>{children}</div>;
}

export function CardFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <footer className={cn("px-6 pb-6 pt-2 flex items-center gap-3", className)}>{children}</footer>
  );
}
